import React, { useMemo } from 'react';

/**
 * Scrolling ticker of live system events synthesized from:
 *  - insights (high-severity → shown first, with up/down direction from impact)
 *  - offlineNotices (source degradation)
 *  - connectors (recent status flips)
 *
 * Pure CSS marquee (no JS timers per item) so it stays cheap on slow devices.
 */
export default function AtlasTickerBar({ insights, offlineNotices, connectors, tick }) {
  const items = useMemo(() => {
    const out = [];
    (insights || []).slice(0, 20).forEach((i) => {
      const sev = Math.round((i.severity || 0) * 100);
      const dir = (i.delta || 0) >= 0 ? 'up' : 'down';
      out.push({
        kind: 'issue',
        sym: (i.domain || i.sector || '?').slice(0, 4).toUpperCase(),
        text: (i.title || i.headline || '').slice(0, 60),
        sev,
        dir,
      });
    });
    (offlineNotices || []).slice(0, 10).forEach((n) => {
      out.push({
        kind: 'offline',
        sym: 'OFF',
        text: `${n.source || n.name || 'source'} offline — ${n.reason || n.message || 'no response'}`.slice(0, 80),
        sev: 100,
        dir: 'down',
      });
    });
    Object.entries(connectors || {}).slice(0, 12).forEach(([name, c]) => {
      const status = (c?.status || c?.state || '').toUpperCase();
      if (!status || status === 'LIVE' || status === 'OK') return;
      out.push({
        kind: 'conn',
        sym: name.slice(0, 5).toUpperCase(),
        text: `connector ${status.toLowerCase()}`,
        sev: 0,
        dir: status === 'DEGRADED' ? 'down' : 'up',
      });
    });
    if (out.length === 0) {
      out.push({ kind: 'idle', sym: 'ATL', text: 'all systems nominal · no urgent events', sev: 0, dir: 'up' });
    }
    return out;
  }, [insights, offlineNotices, connectors]);

  // Duplicate the items once so the marquee loop is seamless.
  const render = [...items, ...items];

  return (
    <div className="atl-ticker" data-testid="atl-ticker" data-tick={tick}>
      <div className="atl-ticker-label">LIVE FEED</div>
      <div className="atl-ticker-track">
        <div className="atl-ticker-content">
          {render.map((it, idx) => (
            <span className="atl-ticker-item" key={idx}>
              <span className="sym">{it.sym}</span>
              <span className={it.dir}>{it.dir === 'up' ? '▲' : '▼'}</span>
              <span>{it.text}</span>
              {it.sev > 0 && <span style={{ color: 'var(--studio-text-2)' }}>· sev {it.sev}</span>}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
